import type { FC, ReactNode } from 'react'
import { useEffect, useRef, useId } from 'react'
import { X, Search } from 'lucide-react'

export interface ActionButton {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'danger'
}

type Props = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  wide?: boolean
  actions?: ActionButton[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  searchPlaceholder?: string
}

const variantStyles: Record<string, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  secondary: 'bg-slate-100 text-slate-600 hover:bg-slate-200',
  danger: 'bg-red-50 text-red-600 hover:bg-red-100',
}

const DashboardModal: FC<Props> = ({ open, onClose, title, children, wide, actions, searchQuery, onSearchChange, searchPlaceholder }) => {
  const dialog = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  const titleId = useId()
  useEffect(() => {
    if (!open) return
    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    const currentDialog = dialog.current
    const focusable = () => Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href], select:not(:disabled), [tabindex="0"]') ?? [])
    ;(focusable()[0] ?? dialog.current)?.focus()
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current()
      if (e.key !== 'Tab') return
      const items = focusable()
      const first = items[0]
      const last = items[items.length - 1]
      if (!first) { e.preventDefault(); dialog.current?.focus(); return }
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
      if (previousFocus?.isConnected && (currentDialog?.contains(document.activeElement) || document.activeElement === document.body)) previousFocus.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className='fixed inset-0 z-[1050] flex items-start justify-center pt-10 pb-10 overflow-y-auto'
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialog} role='dialog' aria-modal='true' aria-labelledby={titleId} tabIndex={-1}
        className={`relative bg-white rounded-2xl shadow-xl border border-slate-200 mx-4 w-full ${wide ? 'max-w-4xl' : 'max-w-lg'}`}
        style={{ animation: 'modalFadeIn 0.2s ease-out' }}
      >
        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
          <h3 id={titleId} className='text-base font-bold text-slate-900'>{title}</h3>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            onClick={onClose}
            aria-label='Close'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        {onSearchChange && (
          <div className='px-6 py-3 border-b border-slate-100'>
            <div className='relative'>
              <Search className='w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
              <input
                type='text'
                aria-label={searchPlaceholder || 'Search'}
                value={searchQuery || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder || 'Search...'}
                className='w-full pl-9 pr-3 py-2 text-[11px] border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-blue-300 focus:outline-none transition-colors'
              />
            </div>
          </div>
        )}

        <div className='px-6 py-4 max-h-[70vh] overflow-y-auto'>{children}</div>

        {actions && actions.length > 0 && (
          <div className='flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-100 bg-slate-50'>
            {actions.map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${variantStyles[action.variant || 'secondary']}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardModal
